import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnalysisCharacter, AnalysisScene, AnalysisProp } from '../types';
import { tosThumbnail } from '../utils/tosImage';
import type { ResolvedBaseMetadata } from '../pages/project-detail/utils/baseMetadata';

type ElementType = 'character' | 'scene' | 'prop';

interface AssetGroup<T> {
  name: string;
  states: T[];
  baseMetadata: ResolvedBaseMetadata;
}

/** Status indicator: green = has selected image, yellow = has base image only, red = nothing */
function getGroupStatus(
  states: Array<{ selectedImageUrl?: string }>,
  baseMetadata: ResolvedBaseMetadata,
): 'green' | 'yellow' | 'red' {
  const hasSelected = states.some(s => s.selectedImageUrl);
  if (hasSelected) return 'green';
  if (
    baseMetadata.selectedBaseImageUrl &&
    !baseMetadata.conflictFields.includes('selectedBaseImageUrl')
  ) return 'yellow';
  return 'red';
}

const statusDot: Record<string, React.CSSProperties> = {
  green: { width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 },
  yellow: { width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 },
  red: { width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 },
};

interface MobileAssetListProps {
  tab: ElementType;
  onTabChange: (tab: ElementType) => void;
  charGroups: AssetGroup<AnalysisCharacter>[];
  sceneGroups: AssetGroup<AnalysisScene>[];
  propGroups: AssetGroup<AnalysisProp>[];
  onSelectAsset: (type: ElementType, name: string) => void;
  isDisabled: (name: string) => boolean;
  charCount: number;
  sceneCount: number;
  propCount: number;
}

const TAB_LABEL_KEYS: Record<ElementType, string> = {
  character: 'mobileAsset.character',
  scene: 'mobileAsset.scene',
  prop: 'mobileAsset.prop',
};

const EMPTY_KEYS: Record<ElementType, string> = {
  character: 'mobileAsset.noCharacters',
  scene: 'mobileAsset.noScenes',
  prop: 'mobileAsset.noProps',
};

const MobileAssetList: React.FC<MobileAssetListProps> = ({
  tab, onTabChange, charGroups, sceneGroups, propGroups,
  onSelectAsset, isDisabled, charCount, sceneCount, propCount,
}) => {
  const { t } = useTranslation('common');

  const tabs: { key: ElementType; label: string; count: number }[] = [
    { key: 'character', label: t(TAB_LABEL_KEYS.character), count: charCount },
    { key: 'scene', label: t(TAB_LABEL_KEYS.scene), count: sceneCount },
    { key: 'prop', label: t(TAB_LABEL_KEYS.prop), count: propCount },
  ];

  type CommonState = { selectedImageUrl?: string; episodes?: number[]; state?: string };
  const toCommonGroups = <T extends CommonState>(gs: AssetGroup<T>[], type: ElementType) =>
    gs.map(g => ({
      name: g.name,
      type,
      states: g.states as CommonState[],
      baseMetadata: g.baseMetadata,
    }));

  const groups = tab === 'character'
    ? toCommonGroups(charGroups, 'character')
    : tab === 'scene'
    ? toCommonGroups(sceneGroups, 'scene')
    : toCommonGroups(propGroups, 'prop');

  return (
    <div className="mobile-asset-list">
      {/* Segment Control */}
      <div className="mobile-asset-tabs">
        {tabs.map(t2 => (
          <button
            key={t2.key}
            className={`mobile-asset-tab ${tab === t2.key ? 'active' : ''}`}
            onClick={() => onTabChange(t2.key)}
          >
            {t2.label}
            <span className="mobile-asset-tab-count">{t2.count}</span>
          </button>
        ))}
      </div>

      {/* Asset List */}
      <div className="mobile-asset-items">
        {groups.length === 0 ? (
          <div className="mobile-asset-empty">
            {t(EMPTY_KEYS[tab])}
          </div>
        ) : (
          groups.map(g => {
            const disabled = isDisabled(g.name);
            const status = getGroupStatus(g.states, g.baseMetadata);
            const selectedBaseImageUrl = g.baseMetadata.conflictFields.includes('selectedBaseImageUrl')
              ? ''
              : g.baseMetadata.selectedBaseImageUrl;
            const thumb = g.states.find(s => s.selectedImageUrl)?.selectedImageUrl
              || selectedBaseImageUrl
              || null;
            const stateCount = g.states.length;
            const episodeSet = new Set(g.states.flatMap(s => s.episodes || []));

            return (
              <button
                key={g.name}
                className={`mobile-asset-item ${disabled ? 'disabled' : ''}`}
                onClick={() => onSelectAsset(g.type, g.name)}
              >
                {/* Thumbnail */}
                <div className="mobile-asset-thumb">
                  {thumb ? (
                    <img src={tosThumbnail(thumb, 60)} alt={g.name} />
                  ) : (
                    <div className="mobile-asset-thumb-placeholder">
                      <span>?</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="mobile-asset-info">
                  <div className="mobile-asset-name-row">
                    <span style={statusDot[status]} />
                    <span className={`mobile-asset-name ${disabled ? 'line-through' : ''}`}>
                      {g.name}
                    </span>
                    {disabled && <span className="mobile-asset-disabled-tag">{t('mobileAsset.disabled')}</span>}
                  </div>
                  <div className="mobile-asset-meta">
                    {stateCount > 1 && <span>{t('mobileAsset.stateCount', { count: stateCount })}</span>}
                    {episodeSet.size > 0 && <span>{t('mobileAsset.episodeCount', { count: episodeSet.size })}</span>}
                  </div>
                </div>

                {/* Arrow */}
                <span className="mobile-asset-arrow">&rsaquo;</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MobileAssetList;
